import React, { useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'

// contexts
import { AccountContext } from '@/contexts/account'
import type { AccountContextValue } from '@/contexts/account'

// libis
import {
  setHasRoomManagerPlugin,
  setHasSalesManagerPlugin,
  setHasMobileRequestPlugin,
  setHasCleaningManagerPlugin,
  checkActivePlugin,
  hasSpaceVisibility,
} from '@/libs/plugins'
import { SideMenuItem, SideMenuItemChild } from '../molecules/side-menu-item'
import { HEADER_HEIGHT, SIDE_MENU_WIDTH } from '@/constants/layout'
import { isScreenDenied, shouldBypassHotelPmsPermissionUi } from '@/libs/hotel-pms-permission'

type SideMenuProps = {
  pathName?: string
}

export const SideMenu: React.FC<SideMenuProps> = () => {
  const { t } = useTranslation()
  const { plugins, permissions, permissionsLoaded, permissionsError } = useContext<AccountContextValue>(AccountContext)
  const permissionsPending = !permissionsLoaded && !permissionsError
  const [isHoverSideMenu, setIsHoverSideMenu] = useState<boolean>(false)
  // Plugin judgment
  const hasRoomManagerPlugin = setHasRoomManagerPlugin(plugins)
  const hasSalesManagerPlugin = setHasSalesManagerPlugin(plugins)
  const hasMobileRequestPlugin = setHasMobileRequestPlugin(plugins)
  const hasCleaningManagerPlugin = setHasCleaningManagerPlugin(plugins)
  const couponPluginActive = checkActivePlugin(plugins, 'CU')
  const reportsPluginActive = checkActivePlugin(plugins, 'RPT')
  const mailDeliveryPluginActive = checkActivePlugin(plugins, 'MD')
  const canView = (screen: Parameters<typeof isScreenDenied>[1]) =>
    shouldBypassHotelPmsPermissionUi() || !isScreenDenied(permissions, screen, permissionsPending ? 'allow' : 'deny')

  return (
    <React.Fragment>
      <div css={sideMenuContainerStyle} onMouseLeave={() => setIsHoverSideMenu(false)} onMouseOver={() => setIsHoverSideMenu(true)}>
        <div className="logoContainerStyle">
          <img src={require('@/static/images/icon_logo.svg')} className="logoStyle" />
          <img src={require('@/static/images/mobile_logo.svg')} className="mobileLogoStyle" />
        </div>
        <div className="sideMenuContentStyle">
          {canView('dashboard') && (
            <SideMenuItem to="/dashboard" title={t('Dashboard')} svgIconPath="icon_dashboard.svg" isHover={isHoverSideMenu} />
          )}
          {canView('reservation') && (
            <SideMenuItem
              to="/accommodation-management?tabType=checkin&refresh=1"
              title={t('Accommodation-management')}
              svgIconPath="icon_stay.svg"
              activePattern="/checkin/|/stay/|/checkout/|/accommodation-management|/entire-search"
              isHover={isHoverSideMenu}
            />
          )}

          {canView('customer') && (
            <SideMenuItem
              to="/customer/list"
              title={t('Customer-management')}
              svgIconPath="icon_customer.svg"
              activePattern="/customer/"
              isHover={isHoverSideMenu}
            />
          )}

          {hasRoomManagerPlugin && canView('guest_room') && (
            <>
              <SideMenuItem
                title={t('Room-manager')}
                svgIconPath="room/room.svg"
                activePattern="/room-manager/"
                isHover={isHoverSideMenu}
                to="/room-manager/daily"
              >
                <SideMenuItemChild to="/room-manager/daily" title={t('Assignment')} isHover={isHoverSideMenu}></SideMenuItemChild>
                <SideMenuItemChild to="/room-manager/indicator" title={t('Indicator')} isHover={isHoverSideMenu}></SideMenuItemChild>
              </SideMenuItem>
            </>
          )}
          {/* 売上・売掛メニューは売上プラグイン有効時のみ表示。画面権限（sales / accounts_receivable）は個別に判定 */}
          {hasSalesManagerPlugin && canView('sales') && (
            <SideMenuItem
              to="/sales-manager"
              title={t('Sales-management')}
              svgIconPath="icon_calculator.svg"
              activePattern="^/sales-manager"
              isHover={isHoverSideMenu}
            />
          )}
          {hasSalesManagerPlugin && canView('accounts_receivable') && (
            <SideMenuItem
              to="/accounts-receivable-manager"
              title={t('A/R Management')}
              svgIconPath="icon_menu_invoice.svg"
              activePattern="^(/accounts-receivable-manager|/invoice-manager)"
              isHover={isHoverSideMenu}
            />
          )}
          {hasMobileRequestPlugin && canView('reservation') && (
            <SideMenuItem
              to="/request-manager"
              title={t('Room service')}
              svgIconPath="mobile_request/request_icon.svg"
              isHover={isHoverSideMenu}
            />
          )}
          {hasSpaceVisibility(plugins) && canView('reservation') && (
            <SideMenuItem
              to="/space-manager"
              title={t('Space-Management')}
              svgIconPath="space_manager/space_icon.svg"
              isHover={isHoverSideMenu}
            />
          )}
          {canView('restaurant') && (
            <SideMenuItem
              to="/restaurant-manager"
              title={t('RestaurantManager.PageTitle')}
              svgIconPath="reservation-note/icon_meal.svg"
              isHover={isHoverSideMenu}
            />
          )}
          {hasCleaningManagerPlugin && canView('cleaning') && (
            <SideMenuItem
              activePattern="/cleaning-manager"
              title={t('Cleaning-management')}
              svgIconPath="cleaning_manager/icon_menu.svg"
              isHover={isHoverSideMenu}
              to="/cleaning-manager/status"
            >
              <SideMenuItemChild to="/cleaning-manager/status" title={t('Cleaning status')} isHover={isHoverSideMenu} />
              <SideMenuItemChild to="/cleaning-manager/schedule" title={t('Schedule')} isHover={isHoverSideMenu} />
            </SideMenuItem>
          )}
          {couponPluginActive && (
            <SideMenuItem
              to="/coupons-manager"
              title={t('Coupons')}
              svgIconPath="coupons_manager/icon_menu.svg"
              isHover={isHoverSideMenu}
            />
          )}
          {mailDeliveryPluginActive && canView('mail_sms') && (
            <SideMenuItem
              to="/mail-delivery-history"
              title={t('Mail/SMS delivery')}
              svgIconPath="mail-delivery/icon_menu.svg"
              isHover={isHoverSideMenu}
            />
          )}
          {reportsPluginActive && canView('report') && (
            <SideMenuItem
              to="/reports-manager"
              title={t('Reports')}
              svgIconPath="reports_manager/icon_menu.svg"
              isHover={isHoverSideMenu}
            />
          )}
          {canView('survey') && (
            <SideMenuItem to="/survey/summary" title={t('Survey')} svgIconPath="surveys/icon_menu.svg" isHover={isHoverSideMenu} />
          )}
          {canView('operation_log') && (
            <SideMenuItem
              to="/operation-log"
              title={t('Operation Log')}
              svgIconPath="operation-log/icon_menu.svg"
              isHover={isHoverSideMenu}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  )
}

const sideMenuContainerStyle = css({
  width: SIDE_MENU_WIDTH,
  zIndex: 103,
  background: 'white',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  position: 'fixed',
  top: 0,
  left: 0,
  '.logoContainerStyle': {
    width: SIDE_MENU_WIDTH,
    height: HEADER_HEIGHT,
    margin: '0 auto',
    borderBottom: '1px solid #f2f2f2',
    borderRight: '1px solid #f2f2f2',
    marginRight: -1,
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    '.logoStyle': {
      width: 90,
      height: HEADER_HEIGHT,
      margin: '0 auto',
      display: 'none',
    },
    '.mobileLogoStyle': {
      width: 40,
      height: 40,
      margin: '0 auto',
      display: 'block',
    },
  },

  '.sideMenuContentStyle': {
    boxShadow: '0 3px 6px rgba(0,0,0,0.16)',
    height: '100vh',
    flex: 1,
    background: 'white',
    overflow: 'auto',
  },
  ':hover': {
    '.logoContainerStyle': {
      width: 180,
      '.logoStyle': {
        display: 'block',
      },
      '.mobileLogoStyle': {
        display: 'none',
      },
    },
    '.sideMenuContentStyle': {
      width: 179,
    },
  },
})
